import React from 'react';

import { ExpandMore, Refresh } from '@mui/icons-material';
import type { CardProps } from '@mui/material';
import {
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Collapse,
  createSvgIcon,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { CardLevel, LoadingSpinner } from '../../../components';
import { i18n } from '../../../i18n/i18n';

export type GroupJobsCardProps = {
  title: React.ReactNode;
  children: React.ReactNode;
  loading?: boolean;
  isEmpty?: boolean;
  onHeaderClick?: () => void;
  onRefresh?: () => void;
  onExportExcel?: () => void;
} & Omit<CardProps, 'title'>;

const ExcelIcon = createSvgIcon(
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M21.17 3.25Q21.5 3.25 21.76 3.5 22 3.74 22 4.08V19.92Q22 20.26 21.76 20.5 21.5 20.75 21.17 20.75H7.83Q7.5 20.75 7.24 20.5 7 20.26 7 19.92V17H2.83Q2.5 17 2.24 16.76 2 16.5 2 16.17V7.83Q2 7.5 2.24 7.24 2.5 7 2.83 7H7V4.08Q7 3.74 7.24 3.5 7.5 3.25 7.83 3.25M7 13.06L8.18 15.28H9.97L8 12.06L9.93 8.89H8.22L7.13 10.9L7.09 10.96L7.06 11.03Q6.8 10.5 6.5 9.96 6.25 9.43 5.97 8.89H4.16L6.05 12.08L4 15.28H5.78M13.88 19.5V17H8.25V19.5M13.88 15.75V12.63H12V15.75M13.88 11.38V8.25H12V11.38M13.88 7V4.5H8.25V7M20.75 19.5V17H15.13V19.5M20.75 15.75V12.63H15.13V15.75M20.75 11.38V8.25H15.13V11.38M20.75 7V4.5H15.13V7Z" />
  </svg>,
  'Excel',
);

const GroupJobsCard = React.memo(
  React.forwardRef<HTMLDivElement, GroupJobsCardProps>(
    ({ children, title, loading, isEmpty, onHeaderClick, onRefresh, onExportExcel, ...CardProps }, ref) => {
      const [expand, setExpand] = React.useState(true);

      const onExpandClickHandler = React.useCallback((event?: React.MouseEvent<HTMLButtonElement>) => {
        event?.stopPropagation();
        setExpand((prev) => !prev);
      }, []);

      const onHeaderClickHandler = React.useCallback(() => {
        if (onHeaderClick) {
          onHeaderClick();
        } else {
          onExpandClickHandler();
        }
      }, [onHeaderClick, onExpandClickHandler]);

      const onRefreshHandler = React.useCallback(
        (event?: React.MouseEvent<HTMLButtonElement>) => {
          event?.stopPropagation();
          onRefresh?.();
        },
        [onRefresh],
      );
      const onExportExcelHandler = React.useCallback(
        (event?: React.MouseEvent<HTMLButtonElement>) => {
          event?.stopPropagation();
          onExportExcel?.();
        },
        [onExportExcel],
      );

      return (
        <Card ref={ref} {...CardProps}>
          <CardActionArea component="span" onClick={onHeaderClickHandler}>
            <CardHeader
              avatar={
                <IconButton onClick={onExpandClickHandler}>
                  <ExpandMore
                    sx={{ transform: expand ? 'rotate(180deg)' : 'rotate(360deg)', transition: '0.2s ease' }}
                  />
                </IconButton>
              }
              title={title}
              titleTypographyProps={{ variant: 'body1' }}
              action={
                <Stack flexDirection="row" gap={1}>
                  {onExportExcel ? (
                    <IconButton size="large" onClick={onExportExcelHandler}>
                      <ExcelIcon />
                    </IconButton>
                  ) : null}

                  {onRefresh ? (
                    <IconButton size="large" onClick={onRefreshHandler}>
                      <Refresh />
                    </IconButton>
                  ) : null}
                </Stack>
              }
            />
          </CardActionArea>

          <Collapse in={expand}>
            <CardContent sx={{ paddingTop: 1 }}>
              <Collapse in={loading} unmountOnExit>
                <LoadingSpinner size={24} />
              </Collapse>

              <Collapse in={!loading && isEmpty} unmountOnExit>
                <CardLevel variant="outlined" level={1}>
                  <CardContent>
                    <Typography variant="subtitle2" textAlign="center" sx={{ opacity: 0.6 }}>
                      {i18n.t('pages.jobs.no_jobs')}
                    </Typography>
                  </CardContent>
                </CardLevel>
              </Collapse>

              <Collapse in={!loading && !isEmpty}>{children}</Collapse>
            </CardContent>
          </Collapse>
        </Card>
      );
    },
  ),
);

export default GroupJobsCard;
